// Blueprint's Home, "Needs you" (pages/ux-full screens/home.js, #/home): ui-web's views of what waits for
// the person's OK, the month against the goal, what the agents did and who owes what, each from its own
// tool. Each shows only to whoever holds that tool's permission (as Nav.tsx's places ask), and a
// screen holds at most three sections (DESIGN.md §3), so who owes what is then only its figure.
import {
  AGING_BUCKETS,
  money,
  plural,
  type AgentActivityProps,
  type AgingProps,
  type ApprovalListProps,
  type GoalPaceProps,
} from '@flowology/ui-web';
import { HUDDLE_PERMS } from '../Nav.tsx';

export type HomeSection = 'approvals' | 'pace' | 'activity' | 'aging';

/** Each section's tool, and the permissions that may call it (any one). */
export const HOME_SECTIONS: readonly { id: HomeSection; tool: string; may: readonly string[] }[] = [
  // approvals.decide: the owner, the manager, the dispatcher and the bookkeeper
  { id: 'approvals', tool: 'approvals.list', may: ['approvals.decide'] },
  // gp.huddle.view: the huddle's
  { id: 'pace', tool: 'gp.pace', may: HUDDLE_PERMS },
  // agents.view: the owner and the manager
  { id: 'activity', tool: 'agents.activity', may: ['agents.view'] },
  // receivables.view: the whole office
  { id: 'aging', tool: 'receivables.aging', may: ['receivables.view'] },
];

/** The sections a person holding these permissions may see, in the prototype's order. */
export const homeTools = (held: readonly string[]): HomeSection[] =>
  HOME_SECTIONS.filter((s) => s.may.some((p) => held.includes(p))).map((s) => s.id);

/** Whether Home has anything for what they hold: the whole office's, never the warehouse's alone. */
export const seesHome = (held: readonly string[]): boolean => homeTools(held).length > 0;

/** The sections drawn as sections: the first three; the rest show only as their figure. */
export const homeSections = (held: readonly string[]): HomeSection[] => homeTools(held).slice(0, 3);

/** "Good morning" until noon, "Good afternoon" until five, then "Good evening", by the hour given. */
export const greeting = (hour: number): string =>
  hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

export interface HomeData {
  approvals?: ApprovalListProps;
  pace?: GoalPaceProps;
  activity?: AgentActivityProps;
  aging?: AgingProps;
}

export interface HomeFigure {
  id: HomeSection;
  label: string;
  value: string;
  sub: string;
}

/** "October": a range's month, for a figure's label. */
const monthWord = (from: string) =>
  new Intl.DateTimeFormat('en-CA', { month: 'long', timeZone: 'UTC' }).format(new Date(`${from}T12:00:00Z`));

/** The title block's figures, one for each section's answer that has come (at most four, so five fit). */
export function homeFigures(d: HomeData): HomeFigure[] {
  const out: HomeFigure[] = [];
  if (d.approvals) {
    // only what this person may decide (QA-860): a manager isn't counted the owner's money requests
    const n = d.approvals.approvals.filter((a) => a.status === 'pending' && !a.cannotDecide).length;
    out.push({
      id: 'approvals',
      label: 'Needs your OK',
      value: String(n),
      sub: n ? 'to approve' : 'Nothing waits',
    });
  }
  if (d.pace)
    out.push({
      id: 'pace',
      // everything in so far, goal or not: the pace's actual counts only months with a goal (QA-683)
      label: `${monthWord(d.pace.from)} so far`,
      value: money(d.pace.totalCents),
      sub: d.pace.targetCents ? `of ${money(d.pace.targetCents)}` : 'No goal set',
    });
  if (d.aging) {
    const late = AGING_BUCKETS.filter((b) => b !== 'current').reduce((s, b) => s + d.aging!.buckets[b], 0);
    out.push({
      id: 'aging',
      label: 'Owed to you',
      value: money(d.aging.openInvoicesCents),
      sub: late ? `${money(late)} late` : 'Nothing late',
    });
  }
  if (d.activity) {
    // asked and not yet decided (QA-858): a decided one waits for nobody
    const waiting = d.activity.steps.filter((s) => s.mode === 'asked' && !s.decision).length;
    out.push({
      id: 'activity',
      label: 'The agents today',
      value: plural(d.activity.steps.length, 'step'),
      sub: waiting ? `${waiting} waiting for you` : 'None waiting for you',
    });
  }
  return out;
}
